/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Input from '@mui/material/Input';
import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import size from 'lodash/size';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import { TerminalPlatformsEnum } from '../../../shared/entities/terminalPlatform';
import {
  selectLoadingTerminals,
  selectTerminalDetails,
  selectPlatformListItemsUnfiltered,
  TerminalListItem,
  selectSpaceVehicleListItemsUnfilteredAndUnsorted,
} from '../../../store';
import { getTerminalsOnce, setFlightPath } from '../../../store/terminals/actions';
import { hideMissionOverlayMissionListModal, setSelectedTerminalTabIndex } from '../../../store/ui/actions';
import { clearMissionData } from '../../../store/flightLegs/actions';
import { clearMission, FTV_MAP_ID } from '../../../store/maps/actions';
import { isIE11 } from '../../../utils/NavigatorUtils';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';
import { EMPTY_TAB_INDEX_SET_BY_FTV } from '../../../utils/TerminalUtilities';
import MenuItems from '../../common/layout/menu/MenuItems';
import Scrollbars from '../../common/layout/Scrollbars';
import { connectivitySortFunction } from '../../common/table/TableUtils';
import StyledProps from '../../common/theme/AppThemeInterface';
import { TerminalIconStaticMarker } from '../TerminalIconStaticMarker';
import { TerminalIconStaticMarkerStylesForTerminalList } from '../TerminalIconStaticMarkerStyles';
import { NetworkGroup, TerminalDetails } from '../../../shared/entities/terminal';
import { PortalsEnum } from '../../../shared/entities/portal';

const StyledCurrentTail = styled.div`
  z-index: 9;
  position: absolute;
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  font-size: 16px;
  color: white;
  top: 0;
  padding-left: 68px;
  padding-right: 32px;
  left: 0;
  box-sizing: border-box;
  width: 233px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  height: 32px;
  max-height: 32px;
  line-height: 31px;
  border-radius: 4px;
  background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
`;
const DropDownPanel = styled.div`
  background-color: ${({ theme }: StyledProps) => theme.primaryBackgroundColor};
  position: absolute;
  width: 233px;
  min-height: 32px;
  max-height: 300px;
  top: 40px;
  left: 0px;
  z-index: 500;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  box-sizing: border-box;
  padding-top: 4px;
  padding-bottom: 8px;
`;
const StyledList = styled.div`
  min-height: 32px;
`;
const StyledNoTerminalsMessage = styled.div`
  text-align: center;
  letter-spacing: 0.14px;
  font-size: 12px;
  line-height: 32px;
  opacity: 0.8;
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
`;

interface StyledListItemProps {
  isLastItem: boolean;
  isIe11: boolean;
  isSelected: boolean;
}
const StyledListItem = styled.div`
  position: relative;
  display: flex;
  // In IE11, make the last item in the list taller as there is an bug in IE11 with the react-custom-scrollbars where the last item is cut off
  max-height: 42px;
  min-height: 42px;
  max-width: 233px;
  height: 42px;
  overflow: hidden;
  line-height: 42px;
  margin-bottom: 0px;
  margin-top: 0px;
  box-sizing: border-box;
  cursor: pointer;
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  font-size: 16px;
  background-color: ${(props: StyledListItemProps) =>
    props.isSelected
      ? ({ theme }: StyledProps) => theme.secondaryBackgroundColor
      : ({ theme }: StyledProps) => theme.primaryBackgroundColor};

  &:hover {
    background-color: ${({ theme }: StyledProps) => theme.hoveredBackgroundColor};
  }

  .terminal__selector__terminal__list__item__connectivity {
    position: absolute;
    box-sizing: border-box;
    min-height: 26px;
    max-height: 26px;
    height: 26px;
    overflow: hidden;
    margin-top: 8px;
    border-radius: 0px !important;
    margin-left: 8px;
  }
`;
const StyledListItemText = styled.div`
  min-height: 26px;
  max-height: 26px;
  height: 26px;
  line-height: 26px;
  width: 169px;
  max-width: 169px;
  box-sizing: border-box;

  padding-right: 40px;
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  margin-left: 68px;
  margin-top: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledTerminalIconContainer = styled.div`
  display: flex;
  align-items: center;
  width: 64px;
  overflow: hidden;
  height: 26px;
  margin-top: -2px;
`;

const StyledDropDownIconOpen = styled(ChevronRight)`
  transform: rotate(-90deg);
  cursor: pointer;
  right: 6px;
  opacity: 0.4;
  top: 4px;
  position: absolute;
  &:hover {
    opacity: 0.6;
  }
`;
const StyledDropDownIconClose = styled(ChevronRight)`
  transform: rotate(90deg);
  cursor: pointer;
  right: 6px;
  top: 4px;
  opacity: 0.8;
  position: absolute;
  &:hover {
    opacity: 0.6;
  }
`;

const TailSelector = styled.div`
  .terminal__list__selector__input {
    padding-left: 8px !important;
    padding-right: 6px;
    box-sizing: border-box;
    font-size: 16px;
    margin-top: -5px;
    color: white;
    text-overflow: ellipsis;
  }
  .terminal__list__selector__root {
    border: none;
    &:focus {
      border: none;
    }
  }
`;
const TailSelectorInput = styled(Input)`
  height: 34px;
  border-radius: 4px;
  padding-left: 8px !important;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0);
  z-index: 10;
  color: ${({ theme }: StyledProps) => theme.primaryTextColor};
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  font-size: 16px;
  line-height: 34px;
  width: 233px;
`;
const StyledSelectedTailCheck = styled(Check)`
  position: absolute;
  height: 14px;
  right: 10px;
  float: right;
  display: inline-block;
  top: 14px;
  color: ${({ theme }: StyledProps) => theme.buttonSelectedColor};
`;

const TailSelectorContainer = styled.div`
  position: relative;
  height: 32px;
`;

export interface FullTerminalViewSelectorContainerProps extends RouteComponentProps {
  isFullSpaceVehicleView?: boolean;
}

export interface FullTerminalViewSelectorContainerState {
  selectedDatapathUuid?: string;
  typeAheadValue: string;
  terminalListDropDownOpen: boolean;
}

function FullTerminalViewSelectorContainer({
  history,
  isFullSpaceVehicleView,
}: FullTerminalViewSelectorContainerProps) {
  const tailSelectorInput = React.useRef<HTMLInputElement>();
  const [terminalListDropDownOpen, setTerminalListDropDownOpen] = React.useState(false);
  const [typeAheadValue, setTypeAheadValue] = React.useState('');

  const dispatch = useDispatch();

  const terminalListItems = useSelector(selectPlatformListItemsUnfiltered, shallowEqual);
  const spaceVehicleListItems = useSelector(selectSpaceVehicleListItemsUnfilteredAndUnsorted, shallowEqual);
  const terminalDetails = useSelector(selectTerminalDetails, shallowEqual);
  const isRequestingTerminals = useSelector(selectLoadingTerminals, shallowEqual);

  const [listItems, setListItems] = React.useState<TerminalListItem[]>(
    !isFullSpaceVehicleView ? terminalListItems : spaceVehicleListItems,
  );
  const [terminalInfo, setTerminalInfo] = useState<TerminalDetails | undefined>(terminalDetails);

  useEffect(() => {
    dispatch(getTerminalsOnce());
  }, [dispatch]);

  useEffect(() => {
    if (!isFullSpaceVehicleView) {
      setListItems(terminalListItems);
    } else {
      setListItems(spaceVehicleListItems);
    }
  }, [terminalListItems, spaceVehicleListItems, isFullSpaceVehicleView]);

  useEffect(() => {
    if (!isNil(terminalDetails)) {
      setTerminalInfo(terminalDetails);
    }
  }, [terminalDetails]);

  const handleTerminalSelected = (datapathUuid: string) => {
    let newUrlPath = `/${MenuItems.fleet}/${datapathUuid}`;

    if (isFullSpaceVehicleView) {
      newUrlPath = `/${MenuItems.transportMonitoring}/${datapathUuid}`;
    }

    history.replace(newUrlPath);
    dispatch(setSelectedTerminalTabIndex(EMPTY_TAB_INDEX_SET_BY_FTV));
    dispatch(hideMissionOverlayMissionListModal());
    dispatch(clearMission(FTV_MAP_ID)());
    dispatch(clearMissionData(FTV_MAP_ID)());
    setTerminalListDropDownOpen(false);
    setTypeAheadValue('');
    // clear flightpath if changing terminals
    if (datapathUuid !== (selectedTerminal || {}).datapathUuid) {
      // Space Vehicles are only shown on ISAN Portal (i.e. Transport Monitoring, Full Space Vehicle View, etc.)
      const portal =
        selectedTerminal?.platformType === TerminalPlatformsEnum.SpaceVehicle
          ? PortalsEnum.ISAN_PORTAL
          : PortalsEnum.UNIFIED_PORTAL;
      dispatch(setFlightPath([], portal));
    }
  };
  const handleDropdownOpen = () => {
    if (!isNil(tailSelectorInput.current)) {
      tailSelectorInput.current.focus();
    }
    setTerminalListDropDownOpen(true);
  };
  const handleDropdownClose = (event: any) => {
    event.stopPropagation();
    setTerminalListDropDownOpen(false);
    setTypeAheadValue('');
  };
  const handleTerminalTypeAheadChange = (event: any) => {
    setTerminalListDropDownOpen(true);
    setTypeAheadValue(event.target.value);
  };

  const filterFunc = (terminal: TerminalListItem) => {
    const terminalName = get(terminal, 'terminalDisplayName', null);
    const validDatapathUuidsInGroup = size(get(terminal, 'datapathUuidsInGroup', []));
    const terminalNetworkGroup = get(terminal, 'networkGroup', null);
    const terminalPlatformType = get(terminal, 'platformType', null);

    let filterFunction =
      ((validDatapathUuidsInGroup &&
        !isNil(terminalName) &&
        terminalName.toLowerCase().includes(typeAheadValue.toLowerCase())) ||
        !size(typeAheadValue)) &&
      terminalNetworkGroup !== NetworkGroup.LINKWAY; // linkway does not support FTV

    if (isFullSpaceVehicleView) {
      filterFunction = filterFunction && terminalPlatformType === TerminalPlatformsEnum.SpaceVehicle;
    }

    return filterFunction;
  };

  const selectedTerminal =
    !isNil(terminalInfo) && !isNil(terminalInfo.datapathUuid)
      ? listItems.find((item: TerminalListItem) => {
          return item.datapathUuidsInGroup.includes(terminalInfo.datapathUuid);
        })
      : ({} as TerminalListItem);
  return !isNil(selectedTerminal) && size(selectedTerminal.datapathUuidsInGroup) ? (
    <TailSelectorContainer>
      <TailSelector>
        <TailSelectorInput
          disableUnderline
          id="tailSelectorInput"
          classes={{ input: 'terminal__list__selector__input', root: 'terminal__list__selector__root' }}
          onClick={handleDropdownOpen}
          labelwidth={200}
          inputRef={tailSelectorInput}
          value={typeAheadValue}
          onChange={handleTerminalTypeAheadChange}
          startAdornment={
            !isNil(selectedTerminal) && !isRequestingTerminals && !size(typeAheadValue) ? (
              <StyledTerminalIconContainer>
                <TerminalIconStaticMarker
                  datapathUuid={selectedTerminal.datapathUuidsInGroup[0]}
                  platformType={get(selectedTerminal, 'platformType', TerminalPlatformsEnum.Airborne)}
                  connectivityStatus={selectedTerminal.connectivityStatus}
                  markerStyles={TerminalIconStaticMarkerStylesForTerminalList}
                  showRing2={selectedTerminal.showRing2}
                  showRing3={selectedTerminal.showRing3}
                />
              </StyledTerminalIconContainer>
            ) : null
          }
          endAdornment={
            terminalListDropDownOpen ? (
              <StyledDropDownIconOpen
                className="terminal__selector__open__dropdown__button"
                onClick={handleDropdownClose}
              />
            ) : (
              <StyledDropDownIconClose
                className="terminal__selector__close__dropdown__button"
                onClick={handleDropdownOpen}
              />
            )
          }
        />
      </TailSelector>
      <StyledCurrentTail id="ftvAircraftTailNumber">
        {!size(typeAheadValue) && !isNil(selectedTerminal)
          ? get(selectedTerminal, 'terminalDisplayName', isRequestingTerminals ? '' : EMPTY_FIELD_STRING_PLACEHOLDER) ||
            EMPTY_FIELD_STRING_PLACEHOLDER
          : null}
      </StyledCurrentTail>
      <React.Fragment>
        {terminalListDropDownOpen && (
          <ClickAwayListener onClickAway={handleDropdownClose}>
            <DropDownPanel>
              <Scrollbars
                style={{ width: 233, backgroundColor: '#111920' }}
                hideTracksWhenNotNeeded={false}
                autoHeight
                autoHeightMin={32}
                autoHeightMax={300}
                className="app__scrollbar--dropdown"
              >
                <StyledList className="terminal__selector__terminal__list">
                  <React.Fragment>
                    {listItems.filter(filterFunc).sort(connectivitySortFunction).length ? null : (
                      <StyledNoTerminalsMessage className="terminal__selector__terminal__list_no__results__message">
                        (No terminals found)
                      </StyledNoTerminalsMessage>
                    )}
                  </React.Fragment>
                  <React.Fragment>
                    {listItems
                      .filter(filterFunc)
                      .sort(connectivitySortFunction)
                      .map((terminal: TerminalListItem, index: number, array: TerminalListItem[]) => {
                        let connectivityStatus = get(terminal, 'connectivityStatus', 'offline');
                        return (
                          <StyledListItem
                            className="terminal__selector__terminal__list__item"
                            isSelected={get(terminalDetails, 'datapathUuid') === terminal.datapathUuidsInGroup[0]}
                            isIe11={isIE11}
                            isLastItem={index === array.length - 1}
                            key={terminal.datapathUuidsInGroup[0]}
                            onClick={() => {
                              handleTerminalSelected(terminal.datapathUuidsInGroup[0]);
                            }}
                          >
                            <React.Fragment>
                              <TerminalIconStaticMarker
                                datapathUuid={terminal.datapathUuidsInGroup[0]}
                                className="terminal__selector__terminal__list__item__connectivity"
                                platformType={get(terminal, 'platformType', TerminalPlatformsEnum.Airborne)}
                                connectivityStatus={connectivityStatus}
                                markerStyles={TerminalIconStaticMarkerStylesForTerminalList}
                                showRing2={terminal.showRing2}
                                showRing3={terminal.showRing3}
                              />
                            </React.Fragment>
                            <StyledListItemText className="terminal__selector__terminal__list__item__text">
                              {get(terminal, 'terminalDisplayName', EMPTY_FIELD_STRING_PLACEHOLDER) ||
                                EMPTY_FIELD_STRING_PLACEHOLDER}
                            </StyledListItemText>
                            {terminal.datapathUuidsInGroup.includes(get(terminalDetails, 'datapathUuid', '')) ? (
                              <StyledSelectedTailCheck className="terminal__selector__terminal__list__item__text_selected__icon" />
                            ) : null}
                          </StyledListItem>
                        );
                      })}
                  </React.Fragment>
                </StyledList>
              </Scrollbars>
            </DropDownPanel>
          </ClickAwayListener>
        )}
      </React.Fragment>
    </TailSelectorContainer>
  ) : null;
}

export default withRouter(FullTerminalViewSelectorContainer);
